import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Chargement, EnTetePage, IconeInfo, IconePlus, Message } from '../components/charte/Elements';
import ListeMissions from '../components/ListeMissions';
import { AIDE } from '../aide/textesAide';
import { COULEURS } from '../theme/charte';
import { chargerDocuments, DocumentClient } from '../donnees/documents';
import { chargerMissions, Mission } from '../donnees/missions';
import { Categorie, categorieMission, situationMission } from '../donnees/situation';

/* Missions, d'après la maquette Missions.dc.html : compteurs en capitales,
   bouton orange « Nouvelle mission », filtre par état en segments,
   recherche, liste à cinq colonnes (la mission qui attend une signature
   est mise en avant), barre d'information en pied. */

const FILTRES: { cle: 'toutes' | Categorie; libelle: string }[] = [
  { cle: 'toutes', libelle: 'Toutes' },
  { cle: 'collecte', libelle: 'En collecte' },
  { cle: 'decision', libelle: 'À votre décision' },
  { cle: 'livree', libelle: 'Livrées' },
  { cle: 'etude', libelle: 'En étude' },
];

/* Ordre d'affichage : ce qui attend le client d'abord, puis en cours, en
   étude, livrées ; à rang égal, la plus récente en premier. */
const RANG: Record<Categorie, number> = { decision: 0, collecte: 1, etude: 2, livree: 3, autre: 4 };

const sansAccents = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const Missions: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [documents, setDocuments] = useState<DocumentClient[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [erreurDocuments, setErreurDocuments] = useState(false);
  const [filtre, setFiltre] = useState<'toutes' | Categorie>('toutes');
  const [recherche, setRecherche] = useState('');
  const [successMsg, setSuccessMsg] = useState<string>((location.state as any)?.success || '');

  useEffect(() => { document.title = 'Missions — Tribe'; }, []);

  useEffect(() => {
    if ((location.state as any)?.success) window.history.replaceState({}, '');
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!user?.email) return;
    let actif = true;
    Promise.allSettled([chargerMissions(user.email), chargerDocuments()]).then(([m, d]) => {
      if (!actif) return;
      if (m.status === 'fulfilled') { setMissions(m.value); setErreur(''); }
      else setErreur('Impossible de charger vos missions. Vérifiez votre connexion puis rechargez la page.');
      if (d.status === 'fulfilled') setDocuments(d.value); else setErreurDocuments(true);
      setChargement(false);
    });
    return () => { actif = false; };
  }, [user?.email]);

  const lignes = useMemo(() => missions
    .map(m => ({ mission: m, situation: situationMission(m, documents) }))
    .sort((a, b) => RANG[a.situation.categorie] - RANG[b.situation.categorie]
      || (b.mission.createdAt || '').localeCompare(a.mission.createdAt || '')), [missions, documents]);

  const compte = (c: 'toutes' | Categorie) => c === 'toutes' ? lignes.length : lignes.filter(l => l.situation.categorie === c).length;
  const terme = sansAccents(recherche.trim());
  const visibles = lignes
    .filter(l => filtre === 'toutes' || l.situation.categorie === filtre)
    .filter(l => !terme || sansAccents([l.mission.titre, l.mission.description, categorieMission(l.mission)].filter(Boolean).join(' ')).includes(terme));

  const enCollecte = compte('collecte');

  return (
    <div className="page">
      <EnTetePage
        surtitre={chargement ? ' ' : `${lignes.length} mission${lignes.length > 1 ? 's' : ''} · ${enCollecte} en collecte`}
        titre="Missions"
        aide={AIDE.missions}
        actions={<Link to="/missions/nouvelle" className="bouton bouton--grand"><IconePlus />Nouvelle mission</Link>}
      />

      {successMsg && <Message genre="succes" onFermer={() => setSuccessMsg('')}>{successMsg}</Message>}

      <div className="ligne ligne--entre" style={{ gap: '12px' }}>
        <div className="segments" role="group" aria-label="Filtrer par état">
          {FILTRES.map(f => (
            <button key={f.cle} type="button" aria-pressed={filtre === f.cle} onClick={() => setFiltre(f.cle)}>
              {f.libelle} · {chargement ? '…' : compte(f.cle)}
            </button>
          ))}
        </div>
        <label className="recherche">
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COULEURS.texteDiscret} strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <span className="visuellement-cache">Rechercher une mission</span>
          <input type="search" placeholder="Rechercher une mission, une zone…" value={recherche} onChange={e => setRecherche(e.target.value)} />
        </label>
      </div>

      {erreurDocuments && !chargement && (
        <Message genre="attention">Vos documents n'ont pas pu être chargés : les devis à signer peuvent ne pas apparaître.</Message>
      )}

      {chargement ? <Chargement /> : erreur ? <Message genre="erreur">{erreur}</Message> : (
        <ListeMissions variante="missions" lignes={visibles}
          vide={lignes.length === 0 ? 'Aucune mission pour le moment. Décrivez votre besoin avec « Nouvelle mission ».' : 'Aucune mission ne correspond à ce filtre.'} />
      )}

      <div className="barre-info">
        <IconeInfo couleur="#E0A848" />
        <div>
          <strong>Une mission livrée reste vivante.</strong> Vous pouvez demander une mise à jour de n'importe quelle mission
          livrée : Tribe ne refait pas la collecte, il rafraîchit ce qui a changé. Écrivez à{' '}
          <a href="mailto:missions@tribe.sn?subject=Mise%20%C3%A0%20jour%20d%27une%20mission" style={{ color: '#E0A848' }}>missions@tribe.sn</a>.
        </div>
      </div>
    </div>
  );
};

export default Missions;
